import { demoBindOnboardingInfo } from '@/lib/analytics/scribe/auth';
import { trackSignupSuccessful } from '@/lib/analytics/scribe/sign-up';
import { trackOrganisationSurveySubmitted } from '@/lib/analytics/scribe/survey';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { type WebinarTimeslotValue } from '@/atoms/demoAtoms';
import { GET_ONBOARDING_V2_VARIANT } from '@/components/constants/flags';
import { OTHER_SPECIALTY_ID } from '@/components/ui/select/clinician-type-select';
import { type BookingIntent } from '@/container/demo-flow/meeting-booker/meeting-booker.types';
import { DEFAULT_TEMPLATE_AUTO_MODE } from '@/features/settings/defaults/constants';
import type { BookMeetingRequest } from '@/hooks/mutation/useMutateBookMeeting';
import type { CreateTemplateProps } from '@/hooks/mutation/useMutateCreateTemplate';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type ReferralCampaign } from '@/utils/affiliateTracking';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { type OnboardingExperience } from '@/utils/onboardingExperience';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { submitSdrDemoLead } from '@/utils/sdrDemoLead';
import { resolveTranscriptionLanguageDefault } from '@/utils/supportedLanguageFilters';
import { getUtmParams } from '@/utils/utmTracking';
import { submitWebinarSignup } from '@/utils/webinarSignup';
import {
  SignupSource,
  TemplateCategory,
  TemplateVisibility,
  type LanguageCode,
  type OrganisationSize,
  type Region,
} from '@heidi/schema/ml-scribe';
import { GET_USER, type UpdateUserRequest } from '@heidi/workspace/contracts';
import { type QueryUserResponse } from '@heidi/workspace/data';

import { track } from '@/lib/analytics/track';
import type { UseOnboardingAnalyticsReturn } from '@/models/analytics/features/useOnboardingAnalytics';
import type { PersonalizationFormData } from '../personalizationTypes';

// The mapping lives on the growth contracts door; kept here for existing callers.
export { mapClinicSizeToOrganisationSize } from '@heidi/growth/contracts';

/** Clears the durable pending-onboarding mark from the cached user, once the backend has been told the user no longer needs the in-app flow. */
export function clearCachedOnboardingPendingMark(): void {
  reactQueryClient.setQueryData<QueryUserResponse>(
    [GET_USER],
    (cached) => cached && { ...cached, onboarding_pending_at: null }
  );
}

export function buildOnboardingUpdateUserRequest(params: {
  formData: PersonalizationFormData;
  organisationSize: OrganisationSize | undefined;
  phoneNumber: string | null | undefined;
  affiliateCode: string | undefined;
  refCode: string | undefined;
  referralCampaign: string | undefined;
  isEvidenceLaunchEnabled: boolean | undefined;
  signupSource: SignupSource | undefined;
}): UpdateUserRequest {
  const {
    formData,
    organisationSize,
    phoneNumber,
    affiliateCode,
    refCode,
    referralCampaign,
    isEvidenceLaunchEnabled,
    signupSource,
  } = params;

  // useMutateUpdateUser pairs a lone first or last name with '' for the other,
  // which would blank a name the backend already holds.
  const hasBothNames = !!formData.firstName.trim() && !!formData.lastName.trim();
  const organisationInfo = {
    name: formData.clinicName || undefined,
    size: organisationSize,
    role: formData.role ?? undefined,
  };
  const hasOrganisationInfo = Object.values(organisationInfo).some(
    (value) => value !== undefined
  );

  return {
    ...(hasBothNames && {
      first_name: formData.firstName,
      last_name: formData.lastName,
    }),
    country: formData.country?.name,
    // Omitted rather than sent as null, so a resume with no phone number cannot blank one already saved.
    ...(phoneNumber && { phone_number: phoneNumber }),
    specialty_id:
      formData.specialty?.id === OTHER_SPECIALTY_ID ? undefined : formData.specialty?.id,
    other_specialty: formData.otherSpecialty || undefined,
    jurisdiction: formData.jurisdiction || undefined,
    ...(hasOrganisationInfo && { organisation_info: organisationInfo }),
    ehr_provider: formData.emrEhr ?? undefined,
    ...(affiliateCode && { affiliate_code: affiliateCode }),
    ...(refCode && { ref_code: refCode }),
    ...(referralCampaign && {
      referral_campaign: referralCampaign as ReferralCampaign,
    }),
    ...(isEvidenceLaunchEnabled && {
      signup_source: signupSource ?? SignupSource.scribe,
      is_new_user_after_evidence_launch: true,
    }),
  };
}

export async function setUserRegionFromCountry(
  formData: PersonalizationFormData,
  mutateUpdateUserRegion: (region: Region) => Promise<unknown>
): Promise<string | undefined> {
  if (!formData.country?.name) return undefined;

  const region = countriesToRegion[formData.country.name];
  if (!region) {
    throw new Error(
      `Country "${formData.country.name}" has no region mapping. Cannot complete onboarding.`
    );
  }
  const session = (await mutateUpdateUserRegion(region)) as
    | { region?: string }
    | undefined;
  return session?.region;
}

export function buildLanguageSessionSettingsPatch(
  formData: Pick<PersonalizationFormData, 'displayLanguage'>,
  supportedTranscriptionLanguageCodes?: {
    input?: LanguageCode[];
    output?: LanguageCode[];
  }
): Pick<UpdateUserRequest, 'language_time_settings' | 'session_settings'> {
  const inputDefault = resolveTranscriptionLanguageDefault(
    formData.displayLanguage,
    supportedTranscriptionLanguageCodes?.input
  );
  const outputDefault = resolveTranscriptionLanguageDefault(
    formData.displayLanguage,
    supportedTranscriptionLanguageCodes?.output
  );

  return {
    ...(formData.displayLanguage && {
      language_time_settings: { display_language: formData.displayLanguage },
    }),
    session_settings: {
      default_template_id: DEFAULT_TEMPLATE_AUTO_MODE,
      ...(inputDefault && {
        default_input_language_code_list: [inputDefault],
      }),
      ...(outputDefault && {
        default_output_language_code: outputDefault,
      }),
    },
  };
}

export async function saveGeneratedTemplateWithRetry(params: {
  formData: PersonalizationFormData;
  mutateCreateTemplate: (args: {
    input: CreateTemplateProps;
    saveAnyway: boolean;
  }) => Promise<{ data?: { id?: string } }>;
  maxAttempts?: number;
  onFinalFailure: () => void;
}): Promise<string | undefined> {
  const { formData, mutateCreateTemplate, maxAttempts = 2, onFinalFailure } = params;

  if (!formData.generatedTemplateStructure || !formData.generatedTemplateName) {
    return undefined;
  }

  let savedTemplateId: string | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const templateResponse = await mutateCreateTemplate({
        input: {
          name: formData.generatedTemplateName,
          description: '',
          structure_template: formData.generatedTemplateStructure,
          template_visibility: TemplateVisibility.User,
          template_category: TemplateCategory.CONSULT_NOTE_TEMPLATE,
          convert_to_json: false,
          template_html: undefined,
        },
        saveAnyway: false,
      });
      if (templateResponse?.data?.id) {
        savedTemplateId = templateResponse.data.id;
      }
      break;
    } catch {
      if (attempt === maxAttempts) {
        logger.warn('Failed to save generated template after retry');
        onFinalFailure();
      }
    }
  }

  return savedTemplateId;
}

export async function submitTemplateFavoritesSafe(
  submitFavorites: (additionalFavoriteIds?: string[]) => Promise<void>,
  savedTemplateId: string | undefined
): Promise<void> {
  try {
    await submitFavorites(savedTemplateId ? [savedTemplateId] : []);
  } catch {
    logger.warn('Failed to submit template favorites during onboarding completion');
  }
}

export function submitDemoSignupIfEligible(params: {
  formData: PersonalizationFormData;
  ctaOptIn: boolean;
  userEmail: string | undefined;
  experience: OnboardingExperience;
  webinarTimeslot: WebinarTimeslotValue | undefined;
}): void {
  const { formData, ctaOptIn, userEmail, experience, webinarTimeslot } = params;

  if (
    !ctaOptIn ||
    !userEmail ||
    !formData.firstName ||
    !formData.lastName ||
    experience.type !== 'webinar' ||
    !webinarTimeslot
  ) {
    return;
  }

  track({
    event_name: TrackingEventName.WebinarRegistrationSubmitted,
    props: {
      timeslot_label: webinarTimeslot.label,
      region: experience.region,
      onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
    },
  });
  void submitWebinarSignup({
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: userEmail,
    timeslot: webinarTimeslot,
  });
}

/**
 * Fires the SDR demo lead to HubSpot plus its two control analytics events.
 * Shared by the request-demo variant (`submitRequestDemoIfEligible`) and the
 * webinar variant's optional demo-lead capture (`submitWebinarDemoLeadIfEligible`).
 *
 * Callers own the eligibility guards (opt-in, email, name, country, reason,
 * phone validity, on-prem); this helper assumes they have already passed and
 * that `formData.country` is non-null.
 */
function dispatchSdrDemoLead(params: {
  formData: PersonalizationFormData;
  countryName: string;
  countryCode?: string;
  trimmedReason: string;
  userEmail: string;
  organisationSize: OrganisationSize | undefined;
}): void {
  const {
    formData,
    countryName,
    countryCode,
    trimmedReason,
    userEmail,
    organisationSize,
  } = params;

  track({
    event_name: TrackingEventName.SdrDemoRequested,
    props: {
      has_reason: true,
      country: countryName,
      country_code: countryCode,
      onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
    },
  });

  // DEV-11979: control event for the phone-collection demo flow. Fires
  // alongside `sdr_demo_requested` whenever the SDR lead actually leaves the
  // client (gated by the same opt-in + reason + country guards above).
  track({
    event_name: TrackingEventName.PhoneDemoBooked,
    props: {
      screen_name: 'onboarding_user_survey',
      country: countryName,
      ...(formData.displayLanguage && { language: formData.displayLanguage }),
    },
  });

  void submitSdrDemoLead({
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: userEmail,
    country: countryName,
    countryCode,
    phone: formData.phoneNumber || undefined,
    company: formData.clinicName || undefined,
    role: formData.role ?? undefined,
    specialty: formData.specialty?.name,
    organisationSize: organisationSize ? String(organisationSize) : undefined,
    demoReason: trimmedReason,
    utmParams: getUtmParams(),
  });
}

export function submitRequestDemoIfEligible(params: {
  formData: PersonalizationFormData;
  ctaOptIn: boolean;
  demoReason: string;
  userEmail: string | undefined;
  experience: OnboardingExperience;
  organisationSize: OrganisationSize | undefined;
  /**
   * True when the user opted into a webinar but no sessions were available, so
   * the phone + reason fallback was shown. Lets a `webinar` experience submit
   * through the SDR path (otherwise reserved for `request-demo`).
   */
  webinarBookedOut?: boolean;
}): void {
  const {
    formData,
    ctaOptIn,
    demoReason,
    userEmail,
    experience,
    organisationSize,
    webinarBookedOut = false,
  } = params;

  const trimmedReason = demoReason.trim();

  // The SDR lead path serves both the standard request-demo CTA and the
  // webinar booked-out fallback (where the user left a phone + reason because
  // no sessions were bookable).
  const isEligibleExperience =
    experience.type === 'request-demo' ||
    (experience.type === 'webinar' && webinarBookedOut);

  if (
    !ctaOptIn ||
    !userEmail ||
    !formData.firstName ||
    !formData.lastName ||
    !formData.country?.name ||
    !isEligibleExperience ||
    !trimmedReason
  ) {
    return;
  }

  // ON-PREM-GATE: belt-and-braces per F1 — even though `country-config` is
  // gated by PostHog (which doesn't initialize on-prem), an explicit check
  // here ensures a leaked PostHog key alone can't trigger a HubSpot lead
  // submission from an airgapped deployment.
  if (isOnPrem) return;

  dispatchSdrDemoLead({
    formData,
    countryName: formData.country.name,
    countryCode: formData.country.countryCode,
    trimmedReason,
    userEmail,
    organisationSize,
  });
}

/**
 * Webinar variant — optional demo-lead capture alongside the timeslot picker.
 * The active (sessions-available) webinar opt-in collects a phone number +
 * reason next to the timeslot dropdown. When both are supplied (valid phone +
 * non-empty reason), submit the same SDR demo lead the request-demo variant
 * fires, in addition to the webinar signup. The fields are optional: when
 * they're blank or the phone is invalid this is a no-op and only the webinar
 * signup is submitted.
 *
 * The booked-out webinar fallback (no sessions, phone + reason shown instead of
 * the picker) is handled by `submitRequestDemoIfEligible` via its
 * `webinarBookedOut` flag, so this function skips that case to avoid a
 * double submission.
 */
export function submitWebinarDemoLeadIfEligible(params: {
  formData: PersonalizationFormData;
  ctaOptIn: boolean;
  demoReason: string;
  userEmail: string | undefined;
  experience: OnboardingExperience;
  organisationSize: OrganisationSize | undefined;
  /** When true the booked-out fallback is shown; that path submits via
   * `submitRequestDemoIfEligible`, so this function must not also fire. */
  webinarBookedOut?: boolean;
}): void {
  const {
    formData,
    ctaOptIn,
    demoReason,
    userEmail,
    experience,
    organisationSize,
    webinarBookedOut = false,
  } = params;

  const trimmedReason = demoReason.trim();

  if (
    webinarBookedOut ||
    !ctaOptIn ||
    !userEmail ||
    !formData.firstName ||
    !formData.lastName ||
    !formData.country?.name ||
    experience.type !== 'webinar' ||
    !trimmedReason ||
    !formData.phoneNumber ||
    !isValidPhoneNumber(formData.phoneNumber)
  ) {
    return;
  }

  // ON-PREM-GATE: belt-and-braces per F1 — mirrors the request-demo guard so a
  // leaked PostHog key alone can't trigger a HubSpot lead from an airgapped build.
  if (isOnPrem) return;

  dispatchSdrDemoLead({
    formData,
    countryName: formData.country.name,
    countryCode: formData.country.countryCode,
    trimmedReason,
    userEmail,
    organisationSize,
  });
}

export async function submitDeferredMeetingBookingIfAny(params: {
  bookingIntent: BookingIntent | null;
  userEmail: string | undefined;
  bookMeetingAsync: (input: BookMeetingRequest) => Promise<void>;
}): Promise<void> {
  const { bookingIntent, userEmail, bookMeetingAsync } = params;
  if (!bookingIntent) return;

  // The intent is captured against the booker's `email` prop, but the user's
  // canonical email is only known after the create-or-fetch in
  // `useOnboardingCompletion`. Prefer the freshest known address.
  const intent: BookMeetingRequest = userEmail
    ? { ...bookingIntent, email: userEmail }
    : bookingIntent;

  const slotStartIso = new Date(intent.startTime).toISOString();
  track({
    event_name: TrackingEventName.MeetingBookingSubmitted,
    props: {
      source: 'onboarding_completion',
      onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
    },
  });

  try {
    await bookMeetingAsync(intent);
    track({
      event_name: TrackingEventName.MeetingBooked,
      props: {
        source: 'onboarding_completion',
        slot_start_iso: slotStartIso,
      },
    });
  } catch (err) {
    logger.error('Deferred meeting booking failed during onboarding:', err);
    track({
      event_name: TrackingEventName.MeetingBookingFailed,
      props: {
        source: 'onboarding_completion',
        // Use error class name (e.g. 'Error', 'TypeError') — raw messages
        // from HubSpot can carry PHI (e.g. the registrant's email).
        error_type: err instanceof Error ? err.name : undefined,
      },
    });
  }
}

export type SendPracticeInvitesFn = (
  emails: string[]
) => Promise<{ successCount: number; failedEmails: string[] } | null>;

export type DeferredPracticeInviteOutcome = 'skipped' | 'sent' | 'partial' | 'failed';

/**
 * Invite emails are collected on the invite-team step before completion.
 * Send only after the organisation_info PATCH has provisioned the Practice team.
 */
export async function submitDeferredPracticeInvitesIfAny(params: {
  inviteEmails: string[];
  sendInvites: SendPracticeInvitesFn;
  settleTeam: () => Promise<boolean>;
}): Promise<DeferredPracticeInviteOutcome> {
  const { inviteEmails, sendInvites, settleTeam } = params;
  if (inviteEmails.length === 0) return 'skipped';

  const ready = await settleTeam();
  if (!ready) {
    logger.warn('Deferred practice invites skipped — Practice team not ready');
    return 'failed';
  }

  const result = await sendInvites(inviteEmails);
  if (!result) {
    logger.warn('Deferred practice invites failed — sendInvites returned null');
    return 'failed';
  }

  if (result.successCount === 0) return 'failed';
  return result.failedEmails.length > 0 ? 'partial' : 'sent';
}

export function trackOnboardingCompletionAnalytics(params: {
  formData: PersonalizationFormData;
  organisationSize: OrganisationSize | undefined;
  analytics: UseOnboardingAnalyticsReturn;
  baaRequired: boolean;
  complianceKitRequired: boolean;
  phoneNumber: string | null | undefined;
}): void {
  const {
    formData,
    organisationSize,
    analytics,
    baaRequired,
    complianceKitRequired,
    phoneNumber,
  } = params;

  trackOrganisationSurveySubmitted({
    name: formData.clinicName || '',
    size: organisationSize,
    role: formData.role ?? undefined,
    ehrProvider: formData.emrEhr ?? undefined,
    specialtyId: formData.specialty?.id,
    specialtyName: formData.specialty?.name,
    otherSpecialty: formData.otherSpecialty || undefined,
    isOfficialPractice: formData.isOfficialPractice,
    cqcPracticeId: formData.cqcPracticeId,
  });

  demoBindOnboardingInfo();
  analytics.trackOnboardingCompleted();
  trackSignupSuccessful({
    baaRequired: !!baaRequired,
    complianceKitRequired: !!complianceKitRequired,
    phoneNumberProvided: !!phoneNumber,
  });
}
